import React, { useState, useEffect } from "react";
import { Navbar } from "../../components/Navbar";
import { Hero } from "../../components/Hero";
import { Features } from "../../components/Features";
import { WhyItMatters } from "../../components/WhyItMatters";
import { HowItWorks } from "../../components/HowItWorks";
import { Capabilities } from "../../components/Capabilities";
import { EarlyAccess } from "../../components/EarlyAccess";
import { Roadmap } from "../../components/Roadmap";
import { FAQ } from "../../components/FAQ";
import { FinalCTA } from "../../components/FinalCTA";
import { Footer } from "../../components/Footer";

export const HomePage: React.FC = React.memo(() => {
    const [showModal, setShowModal] = useState(false);
    const [spotsTaken, setSpotsTaken] = useState(0);
    const [spotsLoading, setSpotsLoading] = useState(true);
    const totalSpots = 1000;
    const walletAddress = 'DFEoPTe4AgoHR8VAkwBsevkfdNjjpQJWPf2Gm4K5Y8KJ';

    useEffect(() => {
        const CACHE_KEY = 'nyktos_spots_data';
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
        const abortController = new AbortController();

        const fetchTransactionCount = async () => {
            // Check for cached data first
            const cachedData = localStorage.getItem(CACHE_KEY);
            if (cachedData) {
                const { spots, timestamp } = JSON.parse(cachedData);
                const now = Date.now();
                
                // If cache is still valid (less than 5 minutes old), use it
                if (now - timestamp < CACHE_DURATION) {
                    setSpotsTaken(spots);
                    setSpotsLoading(false);
                    return;
                }
            }

            // Fetch fresh data if no cache or cache expired
            try {
                const response = await fetch('https://rpc.shyft.to?api_key=ZSx5uYtQbDWuB7yA', {
                    signal: abortController.signal,
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        jsonrpc: '2.0',
                        id: 1,
                        method: 'getSignaturesForAddress',
                        params: [
                            walletAddress,
                            { limit: 1000 }
                        ]
                    })
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                
                if (data.result && Array.isArray(data.result)) {
                    // Count actual transactions
                    const transactionCount = Math.min(data.result.length, 1000);
                    setSpotsTaken(transactionCount);
                    
                    // Cache the result with timestamp
                    localStorage.setItem(CACHE_KEY, JSON.stringify({
                        spots: transactionCount,
                        timestamp: Date.now()
                    }));
                } else {
                    // No transactions found - show 0
                    setSpotsTaken(0);
                    localStorage.setItem(CACHE_KEY, JSON.stringify({
                        spots: 0,
                        timestamp: Date.now()
                    }));
                }
            } catch (error) {
                // Don't process if the request was aborted
                if (error.name === 'AbortError') {
                    return;
                }
                console.error('Error fetching transactions:', error);
                // Try fallback to public RPC
                try {
                    const response = await fetch('https://api.mainnet-beta.solana.com', {
                        signal: abortController.signal,
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            jsonrpc: '2.0',
                            id: 1,
                            method: 'getSignaturesForAddress',
                            params: [walletAddress, { limit: 1000 }]
                        })
                    });
                    const data = await response.json();
                    if (data.result && Array.isArray(data.result)) {
                        const count = Math.min(data.result.length, 1000);
                        setSpotsTaken(count);
                        localStorage.setItem(CACHE_KEY, JSON.stringify({
                            spots: count,
                            timestamp: Date.now()
                        }));
                    } else {
                        setSpotsTaken(0);
                    }
                } catch (fallbackError) {
                    console.error('Fallback also failed:', fallbackError);
                    setSpotsTaken(0);
                }
            } finally {
                setSpotsLoading(false);
            }
        };

        fetchTransactionCount();

        // Cleanup function to abort the request if component unmounts
        return () => {
            abortController.abort();
        };
    }, []);
    
    return (
        <div className="min-h-screen bg-black text-white">
            <Navbar onOpenModal={() => setShowModal(true)} />
            <Hero onOpenModal={() => setShowModal(true)} spotsTaken={spotsTaken} totalSpots={totalSpots} loading={spotsLoading} />
            <Features />
            <WhyItMatters />
            <HowItWorks />
            <Capabilities />
            <EarlyAccess showModal={showModal} setShowModal={setShowModal} spotsTaken={spotsTaken} totalSpots={totalSpots} loading={spotsLoading} />
            <Roadmap />
            <FAQ />
            <FinalCTA onOpenModal={() => setShowModal(true)} />
            <Footer />
        </div>
    );
});
